import React from 'react';

import { v4 as uuidv4 } from 'uuid';

export function generateId(id?: string) {
  return id ? id : uuidv4();
}

export function useGenerateId(newId?: string) {
  const [id, updateId] = React.useState<string>(() => generateId(newId));

  React.useEffect(() => {
    if (newId) {
      updateId(generateId(newId));
    }
  }, [newId]);

  return id;
}

export function omit(props, obj) {
  return props.reduce(
    (newObj, val) => (({ [val]: dropped, ...rest }) => rest)(newObj),
    obj
  );
}

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };

export type XOR<T, U> = T | U extends object
  ? (Without<T, U> & U) | (Without<U, T> & T)
  : T | U;

export function usePrevious(value) {
  const ref = React.useRef();
  React.useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function debounce(func, wait) {
  let timeout;
  function debounced(...args) {
    // tslint:disable-next-line
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func.apply(context, args);
    }, wait);
  }

  debounced.clear = () => {
    clearTimeout(timeout);
  };

  return debounced;
}

// Based on https://stackoverflow.com/a/24394376
let cachedType;

export function detectScrollType() {
  if (cachedType) {
    return cachedType;
  }

  const dummy = document.createElement('div');
  dummy.appendChild(document.createTextNode('ABCD'));
  dummy.dir = 'rtl';
  dummy.style.fontSize = '14px';
  dummy.style.width = '4px';
  dummy.style.height = '1px';
  dummy.style.position = 'absolute';
  dummy.style.top = '-1000px';
  dummy.style.overflow = 'scroll';

  document.body.appendChild(dummy);

  cachedType = 'reverse';

  if (dummy.scrollLeft > 0) {
    cachedType = 'default';
  } else {
    dummy.scrollLeft = 1;
    if (dummy.scrollLeft === 0) {
      cachedType = 'negative';
    }
  }
}

export function getNormalizedScrollLeft(element, direction) {
  const scrollLeft = element.scrollLeft;

  // Perform the calculations only when direction is rtl to avoid messing up the ltr bahavior
  if (direction !== 'rtl') {
    return scrollLeft;
  }

  const type = detectScrollType();

  switch (type) {
    case 'negative':
      return (
        Number(element.scrollWidth) -
        Number(element.clientWidth) +
        Number(scrollLeft)
      );
    case 'reverse':
      return (
        Number(element.scrollWidth) -
        Number(element.clientWidth) -
        Number(scrollLeft)
      );
    default:
      return scrollLeft;
  }
}

function easeInOutSin(time) {
  return (1 + Math.sin(Math.PI * time - Math.PI / 2)) / 2;
}

export function animate(property, element, to, options: any = {}) {
  const { ease = easeInOutSin, duration = 300 } = options;

  let start = null;
  const from = Number(element[property]);
  let cancelled = false;

  const cancel = () => {
    cancelled = true;
  };

  const step = timestamp => {
    if (cancelled) {
      return;
    }

    if (start === null) {
      start = timestamp;
    }
    const time = Math.min(1, (timestamp - start) / duration);

    element[property] = ease(time) * (to - from) + from;

    if (time >= 1) {
      requestAnimationFrame(() => {});
      return;
    }

    requestAnimationFrame(step);
  };

  if (from === to) {
    return cancel;
  }

  requestAnimationFrame(step);
  return cancel;
}

export function assignRef(ref, value) {
  if (ref === null) return;
  if (typeof ref === 'function') {
    ref(value);
  } else {
    try {
      ref.current = value;
    } catch (error) {
      throw new Error(`Cannot assign value "${value}" to ref "${ref}"`);
    }
  }
}

export function useForkedRef(...refs) {
  return React.useMemo(() => {
    if (refs.every(ref => ref === null)) {
      return null;
    }
    return (node: any) => {
      refs.forEach(ref => {
        assignRef(ref, node);
      });
    };
  }, [...refs]);
}

export function stringIncludesUnit(x) {
  return x.includes('px') || x.includes('em') || x.includes('%');
}

export function convertStyleValueToString(
  value: string | number,
  defaultValue?: string
): string {
  return value
    ? typeof value === 'number' ||
      (typeof value === 'string' && !stringIncludesUnit(value))
      ? `${value}px`
      : value
    : defaultValue || 'initial';
}

export const addPxStyleStrings = (
  styleStrings: (string | number)[]
): string => {
  const pxValues: number[] = styleStrings.map(styleString => {
    return parseInt(styleString.toString().replace(/\s*px$/, ''));
  });
  return pxValues.reduce((total, value) => total + value).toString() + 'px';
};

export const removePxStyleStrings = (
  styleStrings: (string | number)[]
): number => {
  const numericValues: number[] = styleStrings.map(item =>
    parseInt(item.toString().replace(/\s*px$/, ''), 10)
  );
  return numericValues.reduce((total, value) => total + value, 0);
};

export function getNodeText(node) {
  if (['string', 'number'].includes(typeof node)) return node;
  if (node instanceof Array) return node.map(getNodeText).join('');
  if (typeof node === 'object' && node) return getNodeText(node.props.children);
}

const candidateSelectors = [
  'input',
  'select',
  'textarea',
  'a[href]',
  'button',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

export function getTrapElements(container): Array<HTMLElement> {
  return Array.from(container.current.querySelectorAll(candidateSelectors));
}

export function toCamelCase(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z 0-9]/gi, '')
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (ltr, index) =>
      index === 0 ? ltr.toLowerCase() : ltr.toUpperCase()
    )
    .replace(/\s+/g, '');
}

type ResolvedProps = {
  [key: string]: any;
};

/**
 * Add keys & values of `defaultProps` that do not exist in `props`
 * @param {object} defaultProps
 * @param {object} props
 * @returns {Person} resolved props
 */
export function resolveProps(
  defaultProps: object,
  props: object
): ResolvedProps {
  const output = { ...props };

  Object.keys(defaultProps).forEach(propName => {
    if (output[propName] === undefined) {
      output[propName] = defaultProps[propName];
    }
  });

  return output;
}

/**
 * Convert ReactNode to string
 * @param {object} defaultProps
 * @returns string
 */
export const reactNodeToString = (node: React.ReactNode): string => {
  if (!node) {
    return '';
  }

  if (typeof node === 'string') {
    return node;
  }

  if (typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(subElement => reactNodeToString(subElement)).join('');
  }

  if (
    typeof node === 'object' &&
    'props' in node &&
    node.props &&
    node.props.children
  ) {
    return reactNodeToString(node.props.children);
  }

  return '';
};

export function collectTextFromReactNode(node: React.ReactNode) {
  const result = [];

  React.Children.forEach(node, child => {
    if (child === null || typeof child === 'boolean') {
      return;
    }

    if (typeof child === 'string' || typeof child === 'number') {
      result.push(child);
    } else if (React.isValidElement(child)) {
      result.push(collectTextFromReactNode(child.props.children));
    }
  });

  return result.join(' ');
}

export const mergeRefs = <T>(...refs: Array<React.Ref<T> | undefined>) => {
  return (node: T | null) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref !== null) {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
};

export function isElementInteractive(element: EventTarget | null): boolean {
  if (!element || !(element instanceof HTMLElement)) return false;

  const tag = element.tagName.toLowerCase();

  if (
    ['button', 'input', 'select', 'textarea', 'a'].includes(tag) ||
    (element.hasAttribute('tabindex') && element.tabIndex >= 0)
  ) {
    if (tag === 'a' && !(element as HTMLAnchorElement).href) return false;

    return !element.hasAttribute('disabled');
  }

  return false;
}

export const handleNumericBeforeInput = (
  e: React.FormEvent<HTMLInputElement>
) => {
  const native = e.nativeEvent as InputEvent;

  if (typeof native.data === 'string' && /\D/.test(native.data)) {
    e.preventDefault();
  }
};

export function hasActiveElementsInside(ref) {
  return (
    Array.from(
      ref.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), video'
      ) || []
    ).filter((element: HTMLElement) => {
      const style = window.getComputedStyle(element);

      return (
        element instanceof HTMLElement &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        !element.hasAttribute('disabled')
      );
    }).length > 0
  );
}

export const descriptionSuffix = '__desc';
export const labelSuffix = '__label';
